import { useEffect, useState } from "react";
import { api, fmt } from "@/lib/api";
import { toast } from "sonner";

const TABS = [
  { id: "etf", label: "ETF FLOWS", tid: "tab-etf" },
  { id: "options", label: "OPTIONS FLOW", tid: "tab-options-flow" },
  { id: "darkpool", label: "DARK POOL", tid: "tab-darkpool" },
  { id: "onchain", label: "ON-CHAIN", tid: "tab-onchain" },
  { id: "whales", label: "WHALES", tid: "tab-whales" },
  { id: "fii", label: "FII / DII", tid: "tab-fii" },
];

export default function AltData() {
  const [tab, setTab] = useState("etf");
  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto" data-testid="altdata-page">
      <div className="border-b border-[color:var(--border-default)] pb-4 mb-6">
        <div className="text-[10px] font-mono-data text-[#FFD700] tracking-widest mb-2">◆ ALTERNATIVE DATA · WHERE ALPHA HIDES</div>
        <h1 className="font-serif-elite text-4xl md:text-5xl font-bold tracking-tight">Alt-Data Terminal</h1>
        <p className="text-[color:var(--text-secondary)] text-sm mt-2 max-w-2xl">
          Dark pool prints, unusual options activity, on-chain crypto metrics, ETF flows, whale venues, and India FII/DII.
        </p>
      </div>

      <div className="flex flex-wrap gap-0 border border-[color:var(--border-default)] mb-4 w-fit" data-testid="altdata-tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            data-testid={t.tid}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-[11px] font-mono-data tracking-widest border-r border-[color:var(--border-default)] last:border-r-0 transition-colors ${
              tab === t.id ? "bg-white text-black" : "text-[color:var(--text-secondary)] hover:bg-[color:var(--bg-hover)]"
            }`}
          >{t.label}</button>
        ))}
      </div>

      {tab === "etf" && <EtfFlowsPanel />}
      {tab === "options" && <SymbolPanel endpoint="options-flow" render={renderOptionsFlow} placeholder="SPY" />}
      {tab === "darkpool" && <SymbolPanel endpoint="dark-pool" render={renderDarkPool} placeholder="AAPL" />}
      {tab === "onchain" && <SymbolPanel endpoint="on-chain" render={renderOnChain} placeholder="BTC" />}
      {tab === "whales" && <SymbolPanel endpoint="whales" render={renderWhales} placeholder="ETH" />}
      {tab === "fii" && <FiiDiiPanel />}
    </div>
  );
}

function EtfFlowsPanel() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);
  useEffect(() => { load(); }, []); // eslint-disable-line
  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/altdata/etf-flows");
      setData(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  if (busy && !data) return <Loading />;
  return (
    <div className="border border-[color:var(--border-default)]" data-testid="etf-flows-table">
      <div className="grid grid-cols-[80px,1fr,80px,80px,80px,110px] gap-2 px-3 py-2 border-b border-[color:var(--border-default)] text-[10px] font-mono-data tracking-widest text-[color:var(--text-muted)]">
        <span>SYMBOL</span><span>NAME</span><span>1MO %</span><span>VOL RATIO</span><span>DIR</span><span>SCORE $M</span>
      </div>
      {(data?.etfs || []).map((e) => (
        <div key={e.symbol} className="grid grid-cols-[80px,1fr,80px,80px,80px,110px] gap-2 px-3 py-2 border-b border-[color:var(--border-default)] text-xs">
          <span className="font-mono-data text-white">{e.symbol}</span>
          <span className="text-[color:var(--text-secondary)]">{e.name}</span>
          <span className={`font-mono-data ${e.price_change_1mo_pct >= 0 ? "text-bull" : "text-bear"}`}>{e.price_change_1mo_pct >= 0 ? "+" : ""}{e.price_change_1mo_pct}%</span>
          <span className="font-mono-data text-white">{e.vol_ratio}×</span>
          <span className={`font-mono-data ${e.estimated_direction === "INFLOW" ? "text-bull" : "text-bear"}`}>{e.estimated_direction}</span>
          <span className={`font-mono-data ${e.flow_score_millions >= 0 ? "text-gold" : "text-bear"}`}>{e.flow_score_millions >= 0 ? "+" : ""}${e.flow_score_millions}M</span>
        </div>
      ))}
      {data?.note && <div className="p-3 text-[10px] font-mono-data text-[color:var(--text-muted)] border-t border-[color:var(--border-default)]">{data.note}</div>}
    </div>
  );
}

function SymbolPanel({ endpoint, render, placeholder }) {
  const [sym, setSym] = useState(placeholder);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async (s = sym) => {
    setBusy(true);
    setData(null);
    try {
      const { data } = await api.get(`/altdata/${endpoint}/${(s || placeholder).toUpperCase()}`);
      setData(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Fetch failed"); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(placeholder); }, []); // eslint-disable-line
  return (
    <div className="space-y-3" data-testid={`sympanel-${endpoint}`}>
      <div className="flex gap-2">
        <input data-testid={`${endpoint}-sym-input`} value={sym} onChange={(e) => setSym(e.target.value.toUpperCase())} placeholder={placeholder} className="flex-1 bg-[color:var(--bg-elevated)] border border-[color:var(--border-default)] px-3 py-2 text-sm font-mono-data text-white focus:outline-none focus:border-[color:var(--border-focus)]" onKeyDown={(e) => e.key === "Enter" && load()} />
        <button data-testid={`${endpoint}-load-btn`} onClick={() => load()} disabled={busy} className="bg-white text-black px-4 text-[11px] font-mono-data tracking-widest hover:opacity-90 disabled:opacity-40">{busy ? "…" : "FETCH"}</button>
      </div>
      {busy && <Loading />}
      {data && render(data)}
    </div>
  );
}

const renderOptionsFlow = (d) => (
  <div className="border border-[color:var(--border-default)]" data-testid="options-flow-table">
    <div className="p-3 border-b border-[color:var(--border-default)] text-[10px] font-mono-data text-gold tracking-widest">◆ {d.symbol} · EXPIRY {d.expiry} · SPOT {d.spot ? `$${d.spot}` : "N/A"}</div>
    <div className="grid grid-cols-[60px,80px,80px,80px,80px,80px,80px] gap-2 px-3 py-2 border-b border-[color:var(--border-default)] text-[10px] font-mono-data tracking-widest text-[color:var(--text-muted)]">
      <span>TYPE</span><span>STRIKE</span><span>VOL</span><span>OI</span><span>V/OI</span><span>IV%</span><span>LAST</span>
    </div>
    {(d.unusual || []).map((r, i) => (
      <div key={`${r.type}-${r.strike}-${i}`} className="grid grid-cols-[60px,80px,80px,80px,80px,80px,80px] gap-2 px-3 py-2 border-b border-[color:var(--border-default)] text-xs">
        <span className={`font-mono-data ${r.type === "call" ? "text-bull" : "text-bear"}`}>{r.type.toUpperCase()}</span>
        <span className="font-mono-data text-white">${r.strike}</span>
        <span className="font-mono-data text-white">{r.volume.toLocaleString()}</span>
        <span className="font-mono-data text-[color:var(--text-secondary)]">{r.open_interest.toLocaleString()}</span>
        <span className="font-mono-data text-gold">{r.vol_oi_ratio}×</span>
        <span className="font-mono-data text-[color:var(--text-secondary)]">{r.iv}%</span>
        <span className="font-mono-data text-white">${r.last}</span>
      </div>
    ))}
  </div>
);

const renderDarkPool = (d) => (
  <div className="border border-[color:var(--border-default)]" data-testid="dark-pool-table">
    <div className="p-3 border-b border-[color:var(--border-default)] text-[10px] font-mono-data text-gold tracking-widest">◆ {d.symbol} · VOLUME ANOMALY DETECTOR</div>
    {(d.prints || []).length === 0 && <div className="p-4 text-xs text-[color:var(--text-muted)] font-mono-data">No unusual volume prints in the last 3 months.</div>}
    {(d.prints || []).map((p) => (
      <div key={p.date} className="grid grid-cols-[100px,100px,80px,80px,80px] gap-2 px-3 py-2 border-b border-[color:var(--border-default)] text-xs">
        <span className="font-mono-data text-white">{p.date}</span>
        <span className="font-mono-data text-[color:var(--text-secondary)]">{p.volume.toLocaleString()}</span>
        <span className="font-mono-data text-gold">{p.ratio}×</span>
        <span className="font-mono-data text-white">${p.close}</span>
        <span className={`font-mono-data ${Math.abs(p.move_pct) < 0.3 ? "text-bull" : "text-[color:var(--text-secondary)]"}`}>{p.move_pct >= 0 ? "+" : ""}{p.move_pct}%</span>
      </div>
    ))}
    <div className="p-3 text-[10px] font-mono-data text-[color:var(--text-muted)] border-t border-[color:var(--border-default)]">{d.note}</div>
  </div>
);

const renderOnChain = (d) => (
  <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border border-[color:var(--border-default)]" data-testid="onchain-panel">
    <MetricCell label="PRICE" value={`$${fmt(d.price_usd)}`} tone="gold" />
    <MetricCell label="MARKET CAP" value={`$${(d.market_cap_usd / 1e9).toFixed(2)}B`} />
    <MetricCell label="24H VOLUME" value={`$${(d.total_volume_usd / 1e9).toFixed(2)}B`} />
    <MetricCell label="24H CHANGE" value={`${d.price_change_24h_pct?.toFixed(2)}%`} tone={d.price_change_24h_pct >= 0 ? "bull" : "bear"} />
    <MetricCell label="7D CHANGE" value={`${d.price_change_7d_pct?.toFixed(2)}%`} tone={d.price_change_7d_pct >= 0 ? "bull" : "bear"} />
    <MetricCell label="30D CHANGE" value={`${d.price_change_30d_pct?.toFixed(2)}%`} tone={d.price_change_30d_pct >= 0 ? "bull" : "bear"} />
    <MetricCell label="1Y CHANGE" value={`${d.price_change_1y_pct?.toFixed(2)}%`} tone={d.price_change_1y_pct >= 0 ? "bull" : "bear"} />
    <MetricCell label="MARKET CAP RANK" value={`#${d.market_cap_rank}`} tone="gold" />
    <MetricCell label="ATH vs NOW" value={`${d.ath_change_pct?.toFixed(1)}%`} tone={d.ath_change_pct > -20 ? "bull" : "bear"} />
    <MetricCell label="CIRCULATING" value={fmt(d.circulating_supply)} />
    <MetricCell label="MAX SUPPLY" value={d.max_supply ? fmt(d.max_supply) : "∞"} />
    <MetricCell label="SENTIMENT ↑" value={`${d.sentiment_votes_up_pct?.toFixed(0) || "?"}%`} tone="bull" />
  </div>
);

const renderWhales = (d) => (
  <div className="border border-[color:var(--border-default)]" data-testid="whales-table">
    <div className="p-3 border-b border-[color:var(--border-default)] text-[10px] font-mono-data text-gold tracking-widest">◆ {d.symbol} · TOP TRADING VENUES</div>
    <div className="grid grid-cols-[1fr,1fr,80px,110px,110px,80px] gap-2 px-3 py-2 border-b border-[color:var(--border-default)] text-[10px] font-mono-data tracking-widest text-[color:var(--text-muted)]">
      <span>EXCHANGE</span><span>PAIR</span><span>PRICE</span><span>VOL 24H</span><span>USD VOL</span><span>SPREAD%</span>
    </div>
    {(d.top_venues || []).map((v, i) => (
      <div key={`${v.exchange}-${i}`} className="grid grid-cols-[1fr,1fr,80px,110px,110px,80px] gap-2 px-3 py-2 border-b border-[color:var(--border-default)] text-xs">
        <span className="text-white">{v.exchange || "—"}</span>
        <span className="font-mono-data text-[color:var(--text-secondary)]">{v.pair}</span>
        <span className="font-mono-data text-white">${fmt(v.price)}</span>
        <span className="font-mono-data text-[color:var(--text-secondary)]">{fmt(v.volume_24h)}</span>
        <span className="font-mono-data text-gold">${fmt(v.converted_volume_usd)}</span>
        <span className="font-mono-data text-[color:var(--text-secondary)]">{v.spread_pct?.toFixed(3) || "—"}</span>
      </div>
    ))}
    <div className="p-3 text-[10px] font-mono-data text-[color:var(--text-muted)] border-t border-[color:var(--border-default)]">{d.note}</div>
  </div>
);

function FiiDiiPanel() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);
  useEffect(() => {
    api.get("/altdata/fii-dii")
      .then(({ data }) => setData(data))
      .catch(() => setData({ history: [], note: "Failed to load NSE data" }))
      .finally(() => setBusy(false));
  }, []);
  if (busy) return <Loading />;
  return (
    <div className="border border-[color:var(--border-default)]" data-testid="fii-dii-table">
      <div className="p-3 border-b border-[color:var(--border-default)] text-[10px] font-mono-data text-gold tracking-widest">◆ INDIA · FII / DII FLOWS (INR CR)</div>
      {(data?.history || []).length === 0 ? (
        <div className="p-4 text-xs text-[color:var(--text-secondary)] font-mono-data">{data?.note || "No data available."}</div>
      ) : (
        <>
          <div className="grid grid-cols-[110px,110px,110px,110px,110px] gap-2 px-3 py-2 border-b border-[color:var(--border-default)] text-[10px] font-mono-data tracking-widest text-[color:var(--text-muted)]">
            <span>DATE</span><span>CATEGORY</span><span>BUY</span><span>SELL</span><span>NET</span>
          </div>
          {data.history.map((r, i) => (
            <div key={`${r.date}-${r.category}-${i}`} className="grid grid-cols-[110px,110px,110px,110px,110px] gap-2 px-3 py-2 border-b border-[color:var(--border-default)] text-xs">
              <span className="font-mono-data text-white">{r.date}</span>
              <span className="font-mono-data text-[color:var(--text-secondary)]">{r.category}</span>
              <span className="font-mono-data text-white">{fmt(r.buy_value)}</span>
              <span className="font-mono-data text-white">{fmt(r.sell_value)}</span>
              <span className={`font-mono-data ${r.net >= 0 ? "text-bull" : "text-bear"}`}>{r.net >= 0 ? "+" : ""}{fmt(r.net)}</span>
            </div>
          ))}
        </>
      )}
      <div className="p-3 text-[10px] font-mono-data text-[color:var(--text-muted)] border-t border-[color:var(--border-default)]">{data?.note}</div>
    </div>
  );
}

function MetricCell({ label, value, tone }) {
  const c = tone === "bull" ? "text-bull" : tone === "bear" ? "text-bear" : tone === "gold" ? "text-gold" : "text-white";
  return (
    <div className="p-3 border-r border-b border-[color:var(--border-default)]">
      <div className="text-[10px] font-mono-data tracking-widest text-[color:var(--text-muted)]">{label}</div>
      <div className={`font-mono-data text-lg mt-1 ${c}`}>{value}</div>
    </div>
  );
}

function Loading() {
  return <div className="p-8 text-center text-xs font-mono-data text-[color:var(--text-muted)]" data-testid="altdata-loading">Loading…</div>;
}
